import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Account created. Welcome, ${u.name}`);
      nav("/onboarding");
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-background text-foreground grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 grid-bg" />
        <Link to="/" className="relative flex items-center gap-2" data-testid="register-back-home">
          <div className="h-8 w-8 rounded-md bg-primary-foreground text-primary grid place-items-center font-heading font-bold">R</div>
          <span className="font-heading font-bold text-lg">RegistryFeed</span>
        </Link>
        <div className="relative">
          <h1 className="font-heading font-black text-4xl leading-tight tracking-tight">
            Join in 30 seconds.<br />Approved within hours.
          </h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Sign up, choose your data package, and get instant access to fresh GST & MCA data every day for 30 days.
          </p>
        </div>
        <div className="relative text-xs text-primary-foreground/70 num tracking-wider">
          — Precision data · Zero fluff
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="register-back-link">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h2 className="font-heading font-bold text-3xl">Create your account</h2>
          <p className="mt-2 text-sm text-muted-foreground">You'll pick your data package next</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={update("name")} data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input id="company" required value={form.company} onChange={update("company")} data-testid="register-company-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" required value={form.phone} onChange={update("phone")} data-testid="register-phone-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required minLength={8} value={form.password} onChange={update("password")} testid="register-password-input" />
              <div className="text-xs text-muted-foreground mt-1">Minimum 8 characters</div>
            </div>
            {err && <div className="text-sm text-destructive" data-testid="register-error">{err}</div>}
            <Button type="submit" className="w-full btn-shine" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-goto-login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
